import { useQueryClient, type QueryClient } from "@tanstack/react-query"
import { useSyncExternalStore } from "react"
import { qk } from "@/shared/api/queryKeys"
import type { ProfileMeta } from "./types"

export function getProfileMeta(
  qc: QueryClient,
  id: string,
): ProfileMeta | undefined {
  return qc.getQueryData<ProfileMeta>(qk.profiles.detail(id))
}

export function patchProfileMeta(
  qc: QueryClient,
  id: string,
  patch: Partial<ProfileMeta>,
): void {
  qc.setQueryData<ProfileMeta>(qk.profiles.detail(id), (prev) => ({
    ...(prev ?? {}),
    ...patch,
  }))
}

export function setProfileMeta(
  qc: QueryClient,
  id: string,
  meta: ProfileMeta,
): void {
  qc.setQueryData<ProfileMeta>(qk.profiles.detail(id), meta)
}

export function removeProfileMeta(qc: QueryClient, id: string): void {
  qc.removeQueries({ queryKey: qk.profiles.detail(id) })
}

export function moveProfileMeta(
  qc: QueryClient,
  from: string,
  to: string,
): void {
  const meta = getProfileMeta(qc, from)
  if (meta) setProfileMeta(qc, to, meta)
  removeProfileMeta(qc, from)
}

export function listTrackedProfileMetas(
  qc: QueryClient,
): Array<[string, ProfileMeta]> {
  return qc
    .getQueriesData<ProfileMeta>({ queryKey: ["profiles", "detail"] })
    .filter((entry): entry is [readonly unknown[], ProfileMeta] => Boolean(entry[1]))
    .map(([key, meta]) => {
      const id = String((key as readonly unknown[])[2] ?? "")
      return [id, meta] as [string, ProfileMeta]
    })
    .filter(([id]) => Boolean(id))
}

let snapshotCache: Record<string, ProfileMeta> = {}
let snapshotSig = ""

function readSnapshot(qc: QueryClient): Record<string, ProfileMeta> {
  const entries = listTrackedProfileMetas(qc)
  const sig = entries
    .map(([id, meta]) => `${id}:${meta.status}:${meta.taskId}:${meta.isRoleplay}:${meta.isLoadingProfileData}:${meta.hasLoadedProfileData}:${meta.errorMessage}:${meta.isCreating}:${meta.isInDatabase}:${meta.isTrackingStatus}:${meta.isRoleplayUpdating}`)
    .join("|")
  if (sig === snapshotSig) return snapshotCache
  snapshotSig = sig
  snapshotCache = Object.fromEntries(entries)
  return snapshotCache
}

/** All profile metas from the Query cache; re-renders on any detail change. */
export function useProfileMetaById(): Record<string, ProfileMeta> {
  const qc = useQueryClient()
  return useSyncExternalStore(
    (onStoreChange) => qc.getQueryCache().subscribe(() => onStoreChange()),
    () => readSnapshot(qc),
    () => ({}),
  )
}

export function useProfileMeta(id: string | undefined): ProfileMeta | undefined {
  const all = useProfileMetaById()
  return id ? all[id] : undefined
}
